import {HubConnection, HubConnectionBuilder, HubConnectionState} from '@microsoft/signalr';
import { action, observable, makeObservable } from 'mobx';
import {Disposers, logger, PromiseThrottler} from '../../../lib/helpers';
import {environment} from '../../../lib/environment';

export class HubConnectionStore {
  readonly connection: HubConnection;

  @observable
  connectionState: HubConnectionState;

  @observable
  dontReconnect: boolean;

  @observable
  dontReconnectMessage: string | null;

  private readonly disposers = new Disposers();

  private readonly throttler = new PromiseThrottler(3000, 5);

  constructor() {
    makeObservable(this);
    this.connectionState = HubConnectionState.Disconnected;
    this.connection = this.buildConnection();
    this.dontReconnect = false;
    this.dontReconnectMessage = null;
  }

  private buildConnection(): HubConnection {
    let url = `${environment.apiServer}/homework/hub`;
    let connection = new HubConnectionBuilder()
      .withUrl(url)
      .build();
    connection.keepAliveIntervalInMilliseconds = 1000;
    connection.onreconnected(this.updateConnectionState);
    connection.onreconnecting(this.updateConnectionState);
    connection.onclose((error) => {
      if (error !== undefined)
        logger.log('HomeworkHubStore.connection.onclose exception', error);
      else
        logger.log('HomeworkHubStore.connection.onclose');
      this.updateConnectionState();
    });
    return connection;
  }

  @action
  start() {
    this.connect();

    let timeout = setInterval(this.updateConnectionState, 500);
    this.disposers.add(() => clearInterval(timeout));
  }

  @action
  private connect() {
    if (this.dontReconnect)
      return;
    if (this.connection.state !== HubConnectionState.Disconnected)
      return;
    this.throttler.run(this.connectionStart)
      .then(this.updateConnectionState)
      .catch(error => {
        logger.log('HomeworkHubStore.connection.start exception', error);
        this.updateConnectionState();
      });
    this.updateConnectionState();
  }

  private connectionStart = (): Promise<void> => {
    if (this.connection.state === HubConnectionState.Connected)
      return Promise.resolve();
    return this.connection.start();
  };
  @action
  private updateConnectionState = () => {
    const connectionState = this.connection.state;
    if (this.connectionState === connectionState)
      return;
    this.connectionState = connectionState;

    if (connectionState === HubConnectionState.Disconnected)
      this.connect();
  };

  reconnect() {
    this.connection.stop()
      .then(this.updateConnectionState)
      .catch((error) => {
        logger.log('HomeworkHubStore.connection.stop exception', error);
        this.updateConnectionState();
      });
  }

  dispose = () => {
    this.dontReconnect = true;
    this.connection.stop()
      .then(this.updateConnectionState)
      .catch((error) => {
        logger.log('HomeworkHubStore.connection.stop exception', error);
        this.updateConnectionState();
      });
    this.disposers.dispose();
  };
}
